// Shared constants, math and deterministic RNG. No DOM, no rendering: the server
// and every client run this exact code.

export const TICK_RATE = 60;
export const DT = 1 / TICK_RATE;
export const SNAPSHOT_EVERY = 2; // 30 Hz snapshots
export const MAX_PLAYERS = 4;
export const LAG_COMP_MAX_TICKS = 12; // 200 ms rewind cap
export const HISTORY_TICKS = 24;
export const INPUT_REDUNDANCY = 6;
export const PROTOCOL_VERSION = 33; // 33: Ev.Breach 76 (flying through house walls); 32: a NetEnemy parts byte (visible damage), the pace; 31: the roster lane (NetEnemy ext byte: affix / cloak / big / rout / redshell, EType.Stalker 18); 30: the rest of the cards (wl0/wl1/killsNoHit/acidBoostT fields, a cowboy flags2 byte, Ev.Strongbox); 29: the elements (el0/el1/elL0/elL1/burnT/acidT/wetT player fields, PickCard carries a weapon slot, EFlag2 frozen/wet/element bits); 28: wildcard cards (hand / offer player fields, C2S.PickCard 32, Ev.Card 72); 27: playtest 1 (robot flags2 byte, OKind.Bolt, bullet drop, left-stick flight); 26: armour and ricochet (Ev.Ricochet 71, AP.Ordnance = 5); 25: class verbs (meleeHold / reflectT / dashN / dashT / pound / poundY player fields, Ev.Blink 69, Ev.Pound 70); 24: the Godot verbs (slide / rollV player fields, the air dive on DodgeK 1, LandK.Prone 3); 23: weight (hitStunT / knockT / knockCd player fields, Ev.Knock 68); 22: the flameout (flameT player field), Godot heat and shield economy; 21: whiskey and the saloon seats meet (both lanes merged; Ev.Drink 66 carries the ditch byte); 20: saloon seats (C2S.Sit, Ev.Seat = 67); 19: whiskey (Ev.Drink = 66, u32 buttons); 18: Ev.Melee gains the blade flourish step; 17: server-run stone falls (Ev.StoneRest), fallen stones and rubble heaps collide, landforms topple; 16: boss registry (run block: boss kind + per-boss payload, island boss kind); 15: destructible buildings + toppling stones (Ev.Building, Ev.Topple, archways in world generation); 14: Ev.Shot flags byte + spin/steady/bloom player fields (per-gun identities); 13: island archetypes, water, landforms (world generation changed); 12: movement lane fall tracking (fallTopY, stepY) + dodge buffer; 11: Ev.FriendlyHit; 10: saloon stage (C2S.Ready, Ev.Stage, Ev.ReadyState), Time grenade slow (timeT); 9: power-ups (player fields, run block, Btn.PowerUp); 8: movement lane (air dash count, move yaw, movement flags, f64 own position); 7: run block (islands, boss, livestock), island in Welcome; 6: posse marks, nemesis; 5: loadouts, grenades, ordnance

export const secs = (s: number) => Math.round(s * TICK_RATE);

export function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v;
}
export function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
export function wrapAngle(a: number) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
export function lerpAngle(a: number, b: number, t: number) {
  return a + wrapAngle(b - a) * t;
}
export function smoothstep(e0: number, e1: number, x: number) {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}
export function len2(x: number, z: number) {
  return Math.sqrt(x * x + z * z);
}
export function len3(x: number, y: number, z: number) {
  return Math.sqrt(x * x + y * y + z * z);
}

/** Forward vector for a yaw/pitch pair. Yaw 0 looks down -Z (three.js convention). */
export function aimDir(yaw: number, pitch: number, out: { x: number; y: number; z: number }) {
  const cp = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cp;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cp;
  return out;
}

// ---------------------------------------------------------------- RNG
export function hash32(a: number, b = 0, c = 0, d = 0): number {
  let h = 0x811c9dc5 ^ a;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h ^= b + 0x9e3779b9 + (h << 6) + (h >>> 2);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= c + 0x9e3779b9 + (h << 6) + (h >>> 2);
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h ^= d + 0x9e3779b9 + (h << 6) + (h >>> 2);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  return (h ^ (h >>> 16)) >>> 0;
}

export class Rng {
  s: number;
  constructor(seed: number) {
    this.s = seed >>> 0 || 1;
  }
  next(): number {
    let t = (this.s = (this.s + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number) {
    return a + (b - a) * this.next();
  }
  int(a: number, b: number) {
    return Math.floor(this.range(a, b + 1));
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  chance(p: number) {
    return this.next() < p;
  }
}

/** One-shot deterministic random in [0,1) from integer keys. */
export function rand01(a: number, b = 0, c = 0, d = 0) {
  return hash32(a, b, c, d) / 4294967296;
}

// ---------------------------------------------------------------- value noise
// Polynomial-only so Node and every browser agree to the last bit.
function lattice(seed: number, x: number, z: number) {
  return hash32(seed, x | 0, z | 0) / 4294967296;
}
export function valueNoise(seed: number, x: number, z: number) {
  const xi = Math.floor(x), zi = Math.floor(z);
  const xf = x - xi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = zf * zf * (3 - 2 * zf);
  const a = lattice(seed, xi, zi), b = lattice(seed, xi + 1, zi);
  const c = lattice(seed, xi, zi + 1), d = lattice(seed, xi + 1, zi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
export function fbm(seed: number, x: number, z: number, oct = 4) {
  let amp = 0.5, f = 1, sum = 0, norm = 0;
  for (let i = 0; i < oct; i++) {
    sum += amp * valueNoise(seed + i * 1013, x * f, z * f);
    norm += amp;
    amp *= 0.5;
    f *= 2.03;
  }
  return sum / norm;
}

export interface V3 {
  x: number;
  y: number;
  z: number;
}
export const v3 = (x = 0, y = 0, z = 0): V3 => ({ x, y, z });

/** Ray vs sphere; returns distance along the (unit) ray or -1. */
export function raySphere(
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
  cx: number, cy: number, cz: number, r: number,
): number {
  const lx = cx - ox, ly = cy - oy, lz = cz - oz;
  const tca = lx * dx + ly * dy + lz * dz;
  const d2 = lx * lx + ly * ly + lz * lz - tca * tca;
  const r2 = r * r;
  if (d2 > r2) return -1;
  const thc = Math.sqrt(r2 - d2);
  const t0 = tca - thc;
  if (t0 >= 0) return t0;
  const t1 = tca + thc;
  return t1 >= 0 ? 0 : -1;
}

/** Distance from point to segment, squared. */
export function segPointDist2(
  ax: number, ay: number, az: number, bx: number, by: number, bz: number,
  px: number, py: number, pz: number,
) {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const apx = px - ax, apy = py - ay, apz = pz - az;
  const ab2 = abx * abx + aby * aby + abz * abz;
  const t = ab2 > 0 ? clamp((apx * abx + apy * aby + apz * abz) / ab2, 0, 1) : 0;
  const qx = ax + abx * t - px, qy = ay + aby * t - py, qz = az + abz * t - pz;
  return qx * qx + qy * qy + qz * qz;
}
